import { useNavigate } from 'react-router-dom';
import { partCategories } from '../../../mocks/products';

interface CategoryGridProps {
  title?: string;
  subtitle?: string;
}

export default function CategoryGrid({
  title = 'Shop by Part Type',
  subtitle = 'Browse our full catalog organized by window covering type — find your part fast',
}: CategoryGridProps) {
  const navigate = useNavigate();

  return (
    <section id="categories" className="bg-stone-50 py-16">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-amber-600 text-xs font-bold tracking-widest uppercase mb-2">
            Full Parts Catalog
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-stone-900 leading-tight">{title}</h2>
          <p className="text-stone-500 mt-2.5 text-sm max-w-lg mx-auto leading-relaxed">{subtitle}</p>
        </div>

        {/* 7-category grid: 4 on top row, 3 on bottom (centered) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          {partCategories.slice(0, 4).map((cat) => (
            <CategoryCard key={cat.id} cat={cat} onClick={() => navigate(cat.slug)} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 lg:grid-cols-3 lg:max-w-3xl lg:mx-auto">
          {partCategories.slice(4).map((cat) => (
            <CategoryCard key={cat.id} cat={cat} onClick={() => navigate(cat.slug)} />
          ))}
        </div>

        {/* Bottom help bar */}
        <div className="mt-10 bg-stone-900 rounded-lg px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="text-white font-bold text-base">Can&apos;t find the part you need?</p>
            <p className="text-stone-400 text-sm mt-1">
              We stock hard-to-find and discontinued parts. Call our specialists for expert help.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              href="tel:+18005551234"
              className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm px-6 py-3 rounded tracking-wide transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line" />
              Call Specialists
            </a>
            <button
              onClick={() => navigate('/about')}
              className="bg-stone-700 hover:bg-stone-600 text-white font-semibold text-sm px-6 py-3 rounded tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryCard({ cat, onClick }: { cat: typeof partCategories[0]; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group bg-white rounded-lg overflow-hidden border border-stone-200 hover:border-amber-400 hover:shadow-none transition-all duration-200 text-left cursor-pointer"
    >
      {/* Image */}
      <div className="w-full h-36 overflow-hidden bg-stone-100 relative">
        <img
          src={cat.image}
          alt={cat.name}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent" />
        <div className="absolute top-2 right-2 bg-amber-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-sm">
          {cat.partCount} parts
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className={`${cat.icon} text-amber-600 text-sm`} />
          </div>
          <h3 className="font-extrabold text-stone-900 text-sm leading-snug group-hover:text-amber-700 transition-colors">
            {cat.name}
          </h3>
        </div>
        <p className="text-stone-500 text-[11px] leading-snug mb-3 ml-8">{cat.description}</p>
        <div className="ml-8 flex items-center gap-1 text-amber-600 text-xs font-bold group-hover:translate-x-1 transition-transform">
          Shop Now <i className="ri-arrow-right-line" />
        </div>
      </div>
    </button>
  );
}
